import axios from 'axios'
import type { AxiosResponse } from 'axios'
import type { ImageData } from '../types'
import { fetchAllPages } from './pagination'

// Resolve API base URL
// Priority: VITE_API_URL (any non-empty) -> window.location.origin -> localhost
const envUrl = (import.meta.env.VITE_API_URL || '').trim()
const resolvedBase = envUrl || (typeof window !== 'undefined' ? window.location.origin : '')
const API_BASE_URL = (resolvedBase || 'http://localhost:8000').replace(/\/+$/, '')

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for auth token
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      // Only redirect to login if we're not already on the login page
      if (!window.location.pathname.includes('/login')) {
        localStorage.removeItem('token')
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Project APIs
export const projectAPI = {
  listPaginated: (params: {
    page?: number
    page_size?: number
    search?: string
    status_filter?: string
    sort_by?: 'completed' | 'name' | 'images' | 'progress' | 'qualified' | 'status' | 'qa_pending'
    sort_order?: 'asc' | 'desc' | 'default'
    assigned_only?: boolean
  }) => api.get('/projects/paginated', { params }),
  create: (data: any) => api.post('/projects/', data),
  get: (id: string) => api.get(`/projects/${id}`),
  update: (id: string, data: any) => api.put(`/projects/${id}`, data),
  delete: (id: string) => api.delete(`/projects/${id}`),
  uploadVideo: (projectId: string, formData: FormData) =>
    api.post(`/projects/${projectId}/upload-video`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  getTaskStatus: (taskId: string) => api.get(`/projects/tasks/${taskId}`),
  getQualifiedProjectIds: () => api.get('/export/batch/qualified'),
}

// Image APIs
const listImages = (projectId: string, skip = 0, limit = 100) =>
  api.get<ImageData[]>(`/projects/${projectId}/images`, { params: { skip, limit } })

export const imageAPI = {
  list: listImages,
  listAll: async (projectId: string): Promise<AxiosResponse<ImageData[]>> => {
    let lastResponse!: AxiosResponse<ImageData[]>
    const data = await fetchAllPages(async (skip, limit) => {
      lastResponse = await listImages(projectId, skip, limit)
      return lastResponse.data
    }, 100)

    return { ...lastResponse, data }
  },
  get: (projectId: string, imageId: string) => api.get(`/projects/${projectId}/images/${imageId}`),
  upload: (projectId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/projects/${projectId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  delete: (projectId: string, imageId: string) =>
    api.delete(`/projects/${projectId}/images/${imageId}`),
}

// Annotation APIs
export const annotationAPI = {
  list: (projectId: string, imageId?: string, userId?: string) =>
    api.get(`/projects/${projectId}/annotations`, {
      params: { image_id: imageId, user_id: userId },
    }),
  getMyProjectAnnotations: (projectId: string) => api.get(`/projects/${projectId}/annotations/my`),
  getMyAnnotation: (projectId: string, imageId: string) =>
    api.get(`/projects/${projectId}/images/${imageId}/annotations/my`),
  saveAnnotation: (
    projectId: string,
    imageId: string,
    data: { annotations: any[]; meta_data?: any; view_settings?: any }
  ) => api.post(`/projects/${projectId}/images/${imageId}/annotations`, data),
  deleteAnnotation: (projectId: string, imageId: string) =>
    api.delete(`/projects/${projectId}/images/${imageId}/annotations`),
  getStats: (projectId: string) => api.get(`/projects/${projectId}/annotations/stats`),
  // Review APIs
  getAllImageAnnotations: (projectId: string, imageId: string) =>
    api.get(`/projects/${projectId}/images/${imageId}/annotations/all`),
  getUserAnnotationStatuses: (projectId: string) =>
    api.get(`/projects/${projectId}/annotations/review-status`),
  approveUserAnnotations: (projectId: string, userId: string) =>
    api.post(`/projects/${projectId}/annotations/users/${userId}/approve`),
  rejectUserAnnotations: (projectId: string, userId: string, data: { reason: string }) =>
    api.post(`/projects/${projectId}/annotations/users/${userId}/reject`, data),
}

// User APIs
export const userAPI = {
  list: () => api.get('/users/'),
  create: (data: any) => api.post('/users/', data),
  get: (id: string) => api.get(`/users/${id}`),
  getCurrent: () => api.get('/users/me'),
  update: (id: string, data: any) => api.put(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
  resetPassword: (id: string, temporaryPassword: string) =>
    api.post(`/users/${id}/reset-password`, { temporary_password: temporaryPassword }),
}

// Analysis APIs
export const analysisAPI = {
  getProjectAnalysis: (projectId: string) => api.get(`/projects/${projectId}/analysis`),
  getImageAnalysis: (projectId: string, imageId: string) =>
    api.get(`/projects/${projectId}/images/${imageId}/analysis`),
  getImageAnnotationsVisualization: (projectId: string, imageId: string) =>
    api.get(`/projects/${projectId}/images/${imageId}/annotations-visualization`),
  getAnnotatorStatistics: (projectId: string) =>
    api.get(`/projects/${projectId}/annotator-statistics`),
  mergeAnnotations: (projectId: string, data: any) =>
    api.post(`/projects/${projectId}/merge-annotations`, data),
}

// Export APIs
export const exportAPI = {
  exportImages: (projectId: string) =>
    api.get(`/export/projects/${projectId}/images`, { responseType: 'blob' }),
  exportMergedAnnotations: (projectId: string) =>
    api.get(`/export/projects/${projectId}/merged-annotations`),
  batchExportImages: (projectIds: string[]) =>
    api.post('/export/batch/images', { project_ids: projectIds }, { responseType: 'blob' }),
  batchExportAnnotations: (projectIds: string[], mergeAnnotations: boolean = true) =>
    api.post(
      '/export/batch/annotations',
      { project_ids: projectIds, merge_annotations: mergeAnnotations },
      { responseType: 'blob' }
    ),
}

// User-Project APIs
export const userProjectAPI = {
  getMyProjects: (skip = 0, limit = 100) =>
    api.get('/user-projects/my-projects', { params: { skip, limit } }),
  getMyCompletedProjects: (skip = 0, limit = 100) =>
    api.get('/user-projects/my-projects/completed', { params: { skip, limit } }),
  getMyProjectsByStatus: (status: string, skip = 0, limit = 100) =>
    api.get(`/user-projects/my-projects/by-status/${status}`, { params: { skip, limit } }),
  getProjectStatus: (projectId: string) => api.get(`/user-projects/project/${projectId}`),
  getProjectStatusWithTransitions: (projectId: string) =>
    api.get(`/user-projects/project/${projectId}/transitions`),
  markProjectComplete: (projectId: string) =>
    api.put(`/user-projects/project/${projectId}/complete`),
  markProjectIncomplete: (projectId: string) =>
    api.put(`/user-projects/project/${projectId}/incomplete`),
  // Admin endpoints
  getProjectUsers: (projectId: string, skip = 0, limit = 100) =>
    api.get(`/user-projects/admin/project/${projectId}/users`, { params: { skip, limit } }),
  assignUsers: (projectId: string, userIds: string[]) =>
    api.post(`/user-projects/admin/project/${projectId}/users`, { user_ids: userIds }),
  unassignUser: (projectId: string, userId: string) =>
    api.delete(`/user-projects/admin/project/${projectId}/users/${userId}`),
}

// Statistics APIs
export const statisticsAPI = {
  getUserStatistics: () => api.get('/statistics/'),
}

// Messages APIs
export const messageAPI = {
  // Create a new message
  sendMessage: (
    projectId: string,
    data: { recipient_id: string; message: string; message_type?: string; image_id?: string }
  ) => api.post(`/messages/projects/${projectId}`, data),

  // Get conversations - unified endpoint
  getConversations: (options?: {
    projectId?: string
    scope?: 'project' | 'all'
    skip?: number
    limit?: number
  }) => {
    const { projectId, scope = 'project', skip = 0, limit = 50 } = options || {}

    const params = new URLSearchParams()
    if (projectId) params.append('project_id', projectId)
    params.append('scope', scope)
    params.append('skip', skip.toString())
    params.append('limit', limit.toString())

    if (scope === 'all') {
      params.delete('project_id') // Remove project_id for all scope
      return api.get(`/messages/all-conversations?${params.toString()}`)
    } else {
      return api.get(`/messages/projects/${projectId}/conversations`)
    }
  },

  // Get messages between current user and another user
  getConversationMessages: (projectId: string, partnerId: string, skip = 0, limit = 50) =>
    api.get(`/messages/projects/${projectId}/conversations/${partnerId}`, {
      params: { skip, limit },
    }),

  // Get all project messages for current user
  getProjectMessages: (projectId: string, skip = 0, limit = 50) =>
    api.get(`/messages/projects/${projectId}`, { params: { skip, limit } }),

  // Get message statistics
  getProjectStats: (projectId: string) => api.get(`/messages/projects/${projectId}/stats`),

  // Unified unread counts endpoint
  getUnreadCounts: (options?: {
    projectId?: string
    includeTotal?: boolean
    includeUserBreakdown?: boolean
  }) => {
    const { projectId, includeTotal = false, includeUserBreakdown = false } = options || {}

    const params = new URLSearchParams()
    if (projectId) params.append('project_id', projectId)
    if (includeTotal) params.append('include_total', 'true')
    if (includeUserBreakdown) params.append('include_user_breakdown', 'true')

    return api.get(`/messages/unread-counts?${params.toString()}`)
  },

  // Mark a message as read
  markAsRead: (messageId: string) => api.patch(`/messages/${messageId}/read`),

  // Mark all messages from a specific user as read
  markConversationAsRead: (projectId: string, partnerId: string) =>
    api.patch(`/messages/projects/${projectId}/conversations/${partnerId}/read`),

  // Batch operations
  markMultipleAsRead: (messageIds: string[]) =>
    api.patch('/messages/batch/read', { message_ids: messageIds }),

  // Get admin users available for messaging
  getAdminUsers: (projectId: string) => api.get(`/messages/projects/${projectId}/admin-users`),
}

export default api
